/**
 * The one Axios instance every service module uses — mirrors
 * `standards/ApiStandards.md`'s envelope exactly: every response is
 * `{success, data, meta?}` or `{success, error: {code, message,
 * details?}}` (`shared/response`, unchanged backend-side). This layer
 * unwraps that envelope so callers work with plain data/`ApiError`,
 * never the raw envelope.
 *
 * Token refresh: `POST /auth/refresh` is real now (login flow
 * overhaul) — `attemptSilentRefresh` below redeems the stored refresh
 * token for a new access/refresh pair, deduplicating concurrent 401s
 * into one in-flight request via the module-level `refreshPromise` (the
 * standard axios-refresh pattern, so 10 simultaneous requests that all
 * 401 at once trigger exactly one refresh call, not 10). Exported so
 * `hooks/useSessionTimeout.ts` can also call it proactively when the
 * access token's `exp` is about to pass, instead of forcing a logout
 * purely because no API call happened to trigger a reactive 401.
 *
 * Environment routing (login flow overhaul): `baseURL` is resolved per
 * request from `store/environment.store.ts` rather than fixed at
 * `axios.create()` time, since a user can pick Production or Training
 * on the login screen — two genuinely separate backend processes/
 * databases (see `ecosystem.config.js`), both same-origin under
 * `csihmh.nexao.in` via nginx path routing (`/api/` vs
 * `/training-api/`), so no CORS config is involved.
 */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorResponse, ApiSuccessResponse } from '@app-types/api.types';
import { ApiError } from '@app-types/api.types';
import { env } from '@core/config/env';
import { useAuthStore } from '@store/auth.store';
import { useEnvironmentStore } from '@store/environment.store';
import { useLoadingStore } from './request-tracker';
import { reportBackendFailure } from './backend-availability';

const TRAINING_API_BASE_URL = '/training-api/v1';
const RECOVERY_API_BASE_URL = '/recovered-api/v1';

export function resolveApiBaseUrl(): string {
  const environment = useEnvironmentStore.getState().environment;
  if (environment === 'training') return TRAINING_API_BASE_URL;
  if (environment === 'recovery') return RECOVERY_API_BASE_URL;
  return env.VITE_API_BASE_URL;
}

let refreshPromise: Promise<RefreshOutcome> | null = null;

function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    const body = axiosError.response?.data;
    if (body && body.success === false) {
      return new ApiError(body.error.code, body.error.message, axiosError.response?.status ?? 0, body.error.details);
    }
    if (axiosError.code === 'ERR_CANCELED') {
      return new ApiError('CLIENT_CANCELED', 'Request was cancelled', 0);
    }
    if (!axiosError.response) {
      return new ApiError('CLIENT_NETWORK_ERROR', 'Unable to reach the server — check your connection', 0);
    }
    return new ApiError('CLIENT_UNKNOWN_ERROR', axiosError.message, axiosError.response.status);
  }
  return new ApiError('CLIENT_UNKNOWN_ERROR', error instanceof Error ? error.message : 'Unknown error', 0);
}

/**
 * Real, complete silent-refresh machinery, deduplicated across
 * concurrent callers via the module-level `refreshPromise`. Called both
 * reactively (a 401 in the response interceptor below) and proactively
 * (`useSessionTimeout`'s token-expiry poll, before it would otherwise
 * force a logout purely because no API call happened to trigger a 401).
 */
/**
 * The refresh token another tab may have just rotated into place.
 *
 * Read from `localStorage` rather than the store, because zustand's `persist`
 * writes there but does NOT notify other tabs — each tab keeps its own
 * in-memory copy until something re-reads.
 */
function persistedRefreshToken(): string | null {
  try {
    const raw = window.localStorage.getItem('nexao-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { tokens?: { accessToken?: string; refreshToken?: string } } };
    return parsed.state?.tokens?.refreshToken ?? null;
  } catch {
    // A malformed or unreadable store is not a reason to end a session; the
    // caller falls back to treating the refresh as failed.
    return null;
  }
}

function persistedAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem('nexao-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { state?: { tokens?: { accessToken?: string; refreshToken?: string } } };
    return parsed.state?.tokens?.accessToken ?? null;
  } catch {
    return null;
  }
}

/**
 * WHY THIS IS THREE OUTCOMES AND NOT A NULLABLE STRING.
 *
 * "No new access token" used to be a single `null`, and every caller read
 * that one value as "the session is over" and logged the user out. But a
 * refresh can fail to complete for reasons that say nothing whatsoever
 * about whether the session is still valid:
 *
 *   - HTTP 429, the rate limiter (this is what was actually logging the
 *     whole hospital out — see the backend's authRefreshPolicy),
 *   - HTTP 502/503 for the seconds a pm2 reload takes during a deploy,
 *   - a dropped wifi frame, a timeout, a laptop lid closing.
 *
 * Only the server explicitly REFUSING the token (401/403 from
 * `/auth/refresh`) proves the session is genuinely finished. Everything
 * else is "ask again later", and must leave the user exactly where they
 * were — mid-form, mid-bill, mid-admission.
 */
export type RefreshOutcome =
  | { status: 'refreshed'; accessToken: string }
  | { status: 'rejected' }
  | { status: 'unavailable' };

const REFRESH_RETRY_DELAYS_MS = [400, 1_200];
const ROTATION_ADOPT_ATTEMPTS = 4;
const ROTATION_ADOPT_DELAY_MS = 250;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * THE ROTATION RACE — why a rejected refresh is still not always a dead session.
 *
 * `POST /auth/refresh` ROTATES: the server replaces the session's stored
 * `refreshTokenHash`, so the token just used stops working immediately.
 * Tokens live in `localStorage` and are therefore shared by every open tab,
 * but each tab dedupes refreshes only WITHIN ITSELF and holds its own
 * in-memory copy.
 *
 * So with two tabs open, both notice the access token expire inside the same
 * poll and both POST the same refresh token. One wins and rotates; the other
 * is told "Invalid or expired refresh token" — and used to treat that as
 * session death, log out, and clear the shared store, killing the tab that
 * had just succeeded.
 *
 * The loser's token is stale, not invalid. If another tab has since written a
 * different one, adopt it. This POLLS rather than reading once, because the
 * winner writes `localStorage` only after its own response lands: reading a
 * single time immediately after our rejection frequently looked at the old
 * value and declared the session dead a few milliseconds too early.
 */
async function adoptRotatedToken(usedRefreshToken: string): Promise<string | null> {
  for (let attempt = 0; attempt < ROTATION_ADOPT_ATTEMPTS; attempt += 1) {
    const rotated = persistedRefreshToken();
    const accessToken = persistedAccessToken();
    if (rotated && rotated !== usedRefreshToken && accessToken) {
      useAuthStore.getState().setTokens({ accessToken, refreshToken: rotated });
      return accessToken;
    }
    await delay(ROTATION_ADOPT_DELAY_MS);
  }
  return null;
}

/** A response the server never produced (network/timeout/abort) is never proof of a dead session. */
function classifyFailure(error: unknown): 'rejected' | 'unavailable' {
  if (!axios.isAxiosError(error)) return 'unavailable';
  const status = error.response?.status;
  if (status === 401 || status === 403) return 'rejected';
  return 'unavailable';
}

async function performRefresh(refreshToken: string): Promise<RefreshOutcome> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await axios.post<ApiSuccessResponse<{ accessToken: string; refreshToken: string }>>(
        `${resolveApiBaseUrl()}/auth/refresh`,
        { refreshToken },
        // This is a BARE axios call, not the configured client, so it does not
        // inherit `withCredentials` and must ask for it itself.
        { withCredentials: true },
      );
      const { accessToken, refreshToken: nextRefreshToken } = res.data.data;
      useAuthStore.getState().setTokens({ accessToken, refreshToken: nextRefreshToken });
      return { status: 'refreshed', accessToken };
    } catch (error) {
      if (classifyFailure(error) === 'rejected') {
        const adopted = await adoptRotatedToken(refreshToken);
        return adopted ? { status: 'refreshed', accessToken: adopted } : { status: 'rejected' };
      }

      // Transient. Retry a couple of times before conceding — and even then
      // concede only to 'unavailable', which keeps the user logged in.
      const backoff = REFRESH_RETRY_DELAYS_MS[attempt];
      if (backoff === undefined) {
        // Another tab may have refreshed successfully while we were failing.
        const adopted = await adoptRotatedToken(refreshToken);
        return adopted ? { status: 'refreshed', accessToken: adopted } : { status: 'unavailable' };
      }
      await delay(backoff);
    }
  }
}

export async function attemptSilentRefresh(): Promise<RefreshOutcome> {
  // Prefer the persisted token over this tab's in-memory copy: another tab may
  // have rotated it since we last read, and using the stale one guarantees a
  // pointless rejection we then have to recover from.
  const refreshToken = persistedRefreshToken() ?? useAuthStore.getState().tokens?.refreshToken;
  if (!refreshToken) {
    return { status: 'rejected' };
  }

  refreshPromise ??= performRefresh(refreshToken).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

export function createHttpClient(): AxiosInstance {
  const client = axios.create({
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
    /**
     * SEND AND ACCEPT COOKIES. Without this the browser neither stores nor
     * returns `Set-Cookie`, and axios defaults it to false.
     *
     * That silently disabled the entire "Remember this device for 30 days"
     * feature. The server side was complete and correct — it issues a signed,
     * httpOnly `nexao_trusted_device` cookie on MFA verify and checks it on
     * every login — but the cookie was dropped on arrival and never sent back,
     * so every login asked for a code again and the trusted-device rows were
     * written and never read.
     *
     * Safe here because the API is SAME-ORIGIN (`/api/v1` through nginx), so
     * no CORS credential negotiation is involved and the cookie's
     * `sameSite: 'strict'` is satisfied.
     */
    withCredentials: true,
  });

  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.baseURL = resolveApiBaseUrl();
    const accessToken = useAuthStore.getState().tokens?.accessToken;
    if (accessToken) {
      config.headers.set('Authorization', `Bearer ${accessToken}`);
    }
    useLoadingStore.getState().increment();
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      useLoadingStore.getState().decrement();
      return response;
    },
    async (error: unknown) => {
      useLoadingStore.getState().decrement();

      if (!axios.isAxiosError(error) || !error.config) {
        const apiError = toApiError(error);
        reportBackendFailure(apiError);
        return Promise.reject(apiError);
      }

      const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
      const status = error.response?.status;

      if (status === 401 && !originalRequest._retried && !originalRequest.url?.includes('/auth/')) {
        originalRequest._retried = true;
        const outcome = await attemptSilentRefresh();
        if (outcome.status === 'refreshed') {
          originalRequest.headers.set('Authorization', `Bearer ${outcome.accessToken}`);
          return client(originalRequest);
        }

        /**
         * ONLY AN EXPLICIT REJECTION ENDS A SESSION, AND ONLY IF IT STILL LOOKS
         * ENDED BY THE TIME WE ACT ON IT.
         *
         * This branch is the one an ACTIVE user hits — being active is what
         * produces the requests that 401 in the first place — so it is exactly
         * where an over-eager logout does the most damage. It used to clear the
         * session on any falsy refresh result, which made a working user's
         * session hostage to a single request: a 429, a pm2 reload mid-deploy,
         * or a lost rotation race were all indistinguishable from a genuinely
         * expired refresh token.
         *
         * 'unavailable' therefore leaves the session intact and simply lets
         * this one request fail; the expiry poll will try again shortly. Even
         * on 'rejected' we re-check expiry first, mirroring the poll: any
         * concurrent refresh writes to the same store, so a token that is no
         * longer expired means the session is alive and this attempt merely
         * lost.
         */
        if (outcome.status === 'rejected' && useAuthStore.getState().isTokenExpired()) {
          useAuthStore.getState().clearSession();
        }
      }

      await revealBlobErrorBody(error);
      const apiError = toApiError(error);
      // A dropped connection or a gateway answer (502/503/504) is what a deploy looks like from here.
      reportBackendFailure(apiError);
      return Promise.reject(apiError);
    },
  );

  return client;
}

/**
 * A FAILED BLOB REQUEST STILL CARRIES THE SERVER'S REASON — RECOVER IT.
 *
 * `responseType: 'blob'` applies to the ERROR body as well as the success one,
 * so a refusal on a PDF endpoint arrives as a Blob. `toApiError` reads
 * `data.error.message`, finds nothing on a Blob, and falls through to axios's
 * own "Request failed with status code 400" — which tells an operator only
 * that something went wrong.
 *
 * Every PDF endpoint in the product is affected: the lab report, the batch
 * print, the specimen label, the invoice and discharge documents. Their servers
 * all answer with the standard `{success, error: {code, message}}` envelope,
 * and that message is the actionable half — "A batch print is limited to 50
 * reports", "This report has no rendered snapshot".
 *
 * Parsing here, in the one place that already turns responses into `ApiError`,
 * keeps every caller unchanged. A body that is not JSON, or not the envelope,
 * is LEFT ALONE — `toApiError` then behaves exactly as it did, so this can only
 * add a message where there was none, never replace a good one with a guess.
 */
async function revealBlobErrorBody(error: AxiosError): Promise<void> {
  const response = error.response;
  if (!response || !(response.data instanceof Blob)) return;
  try {
    const parsed = JSON.parse(await response.data.text()) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as ApiErrorResponse).error?.message === 'string'
    ) {
      response.data = parsed as never;
    }
  } catch {
    // A binary or truncated body is not an envelope. Nothing to reveal.
  }
}

export const httpClient = createHttpClient();

/** Unwraps `ApiSuccessResponse<T>` down to `T` — the shape every service module actually wants. */
export async function unwrap<T>(promise: Promise<{ data: ApiSuccessResponse<T> }>): Promise<T> {
  const response = await promise;
  return response.data.data;
}

/** The `meta.pagination` block every `MasterController.list` response carries. */
export interface PaginationMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
}

/**
 * Retrieves EVERY page of a paginated list endpoint.
 *
 * Fixes defect C-7 (ADR-087 Phase 2). Several "load everything for a picker"
 * call sites asked for `{ page: 1, limit: 100 }` and returned `data` while
 * discarding `meta` — so with 3 rows in the table the truncation was
 * invisible, and the moment the real 310-test laboratory catalogue was
 * imported, 210 tests would have silently vanished from the clinical order
 * picker with no error and no UI signal.
 *
 * The fix is to page to completion using the `totalPages` the API has always
 * returned, NOT to raise the ceiling: `limit: 1000` would fail the server's
 * MAX_LIMIT of 200 with a 422, and even a legal larger number just moves the
 * silent cliff further out instead of removing it.
 *
 * `maxPages` is a runaway guard, not a data limit — exceeding it throws
 * rather than quietly returning a partial list, because a partial clinical
 * catalogue that looks complete is the exact failure this function exists to
 * eliminate.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<{ data: ApiSuccessResponse<T[]> }>,
  options: { limit?: number; maxPages?: number } = {},
): Promise<T[]> {
  const limit = options.limit ?? 200;
  const maxPages = options.maxPages ?? 50;

  const first = await fetchPage(1, limit);
  const items = [...first.data.data];
  const meta = first.data.meta?.pagination as PaginationMeta | undefined;
  const totalPages = meta?.totalPages ?? 1;

  if (totalPages > maxPages) {
    throw new Error(
      `Refusing to page through ${totalPages} pages (max ${maxPages}). ` +
        'Returning a partial list here would silently hide records.',
    );
  }

  for (let page = 2; page <= totalPages; page += 1) {
    const next = await fetchPage(page, limit);
    items.push(...next.data.data);
  }
  return items;
}

export { toApiError };
